<script setup>
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import SelectInput from "@/Components/SelectInput.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import DialogModal from "@/Components/DialogModal.vue";
import { ref, onMounted, watch } from "vue";
import axios from 'axios';
import { useForm } from "@inertiajs/inertia-vue3";
import { computed } from 'vue';
import { useTenantTextRules } from '@/helpers'

const props = defineProps({
  show: Boolean,
  styleId: Number,
  styleNumber: String,
  garmentType: String,
  gender: String,
  client: String,
  colorOptions: Array,
  storages: Array,
  mainSizes: Array,
  optSizes: Array,
});
const { resolveTextRule } = useTenantTextRules();

const styleEntityLabel = computed(() =>
  resolveTextRule('inventory.style.entity', 'Style', 'inventory.add_modal.style_entity')
);
const styleNumberLabel = computed(() =>
  resolveTextRule('style_number.label', 'Style Number', 'inventory.add_modal.style_number')
);
const garmentLabel = computed(() =>
  resolveTextRule('garment.label', 'Garment Type', 'inventory.add_modal.garment')
);
const locationLabel = computed(() =>
  resolveTextRule('location.label', 'Location', 'inventory.add_modal.location')
);
const boxLabel = computed(() =>
  resolveTextRule('inventory.filters.box_label', 'Box', 'inventory.add_modal.box')
);

const emit = defineEmits(['close']);

const form = useForm({
  color: '',
  storageId: '',
  box: '',
  row: '',
  rack: '',
  shelf: '',
  styleId: props.styleId,
  quantities: {},
  conveyor_slot: '',
});

const error = ref(null);

onMounted(() => {
  const q = {};
  for (const main of props.mainSizes) {
    q[main] = {};
    if (props.optSizes.length > 0) {
      for (const opt of props.optSizes) {
        q[main][opt] = '';
      }
    } else {
      q[main]['Qty'] = '';
    }
  }
  form.quantities = q;
});


async function generateBox() {
  try {
    const response = await axios.post('/inventory/generate-box-number', {
      gen: 1
    });

    if (response.data.box_num) {
      form.box = `${String(response.data.box_num).padStart(4, '0')}`;
    }
  } catch (error) {
    console.error(error);
  }
}

function submitInventory() {
  const cleanedQuantities = {}

  for (const main in form.quantities) {
    cleanedQuantities[main] = {}

    for (const opt in form.quantities[main]) {
      const value = form.quantities[main][opt]

      if (value !== '' && value !== null && value !== undefined) {
        cleanedQuantities[main][opt] = value
      }
    }

    if (Object.keys(cleanedQuantities[main]).length === 0) {
      delete cleanedQuantities[main]
    }
  }
  const payload = {
    ...form.data(),
    styleId: props.styleId,
    quantities: cleanedQuantities
  }

  console.log('Payload limpio:', payload)

  form.post(route('inventory.add'), {
    data: payload,
    preserveScroll: true,
    onSuccess: () => {
      emit('close')
      form.reset()
    },
    onError: (err) => {
      console.error(err)
      error.value = err.error || 'An error occurred while adding the inventory.';
    }
  })
}


const selectedStorageType = computed(() => {
  const selected = props.storages.find(s => s.id === form.storageId);
  console.log('Selected Storage Type:', selected ? selected.type : '');
  return selected ? selected.type : '';
}); 

watch(() => props.show, (newVal) => {
  if (newVal) {
    const q = {}
    for (const main of props.mainSizes) {
      q[main] = {}
      if (props.optSizes.length > 0) {
        for (const opt of props.optSizes) {
          q[main][opt] = ''
        }
      } else {
        q[main]['Qty'] = ''
      }
    }
    form.quantities = q
    form.conveyor_slot = ''
    error.value = null
  }
})


</script>

<template>
  <DialogModal :show="show" @close="emit('close')">
    <template #title>
      Add Unit for {{ styleNumber }}
    </template>

    <template #content>
      <form @submit.prevent="submitInventory">
        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-4 text-sm text-gray-700">
            <div><strong>{{ styleEntityLabel }}:</strong> {{ styleNumber }}</div>
            <div><strong>{{ garmentLabel }}:</strong> {{ garmentType }}</div>
            <div><strong>Gender:</strong> {{ gender }}</div>
            <div><strong>Client:</strong> {{ client }}</div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Color:</label>
              <SelectInput v-model="form.color" class="mt-1">
                <option value="">-- Select --</option>
                <option v-for="color in colorOptions" :key="color" :value="color">{{ color }}</option>
              </SelectInput>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">{{ locationLabel }}:</label>
              <SelectInput v-model="form.storageId" class="mt-1">
                <option value="">-- Select --</option>
                <option v-for="storage in storages" :key="storage.id" :value="storage.id">
                  {{ storage.identifier }}_{{ storage.name }}
                </option>
              </SelectInput>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">{{ boxLabel }}:</label>
              <input type="text" v-model="form.box"
                class="border border-gray-300 rounded-md shadow-sm px-3 py-2 w-full" />
            </div>
            <PrimaryButton type="button" @click="generateBox" class="mt-6 btn-blue">
              Generate Box Number
            </PrimaryButton>
          </div>
          <div v-if="error" class="text-red-500 text-sm mt-2">
            {{ error }}
          </div>

          <div class="grid grid-cols-3 gap-4" v-if="selectedStorageType === 'warehouse'">
            <div>
              <label for="row" class="block text-sm font-medium text-gray-700 mb-1">Row</label>
              <input id="row" type="text" v-model="form.row"
                class="border border-gray-300 rounded-md shadow-sm px-3 py-2 w-full" />
            </div>

            <div>
              <label for="rack" class="block text-sm font-medium text-gray-700 mb-1">Rack</label>
              <input id="rack" type="text" v-model="form.rack"
                class="border border-gray-300 rounded-md shadow-sm px-3 py-2 w-full" />
            </div>

            <div>
              <label for="shelf" class="block text-sm font-medium text-gray-700 mb-1">Shelf</label>
              <input id="shelf" type="text" v-model="form.shelf"
                class="border border-gray-300 rounded-md shadow-sm px-3 py-2 w-full" />
            </div>
          </div>

          <div class="grid grid-cols-3 gap-4" v-if="selectedStorageType === 'conveyor'">
            <div>
              <label for="conveyor_slot" class="block text-sm font-medium text-gray-700 mb-1">
                Conveyor Slot
              </label>
              <input id="conveyor_slot" type="number" min="1" v-model.number="form.conveyor_slot"
                class="border border-gray-300 rounded-md shadow-sm px-3 py-2 w-full" />
            </div>
          </div>

          <div class="mt-8 rounded overflow-auto">
            <table class="min-w-full w-full text-left text-sm font-light">
              <thead class="border-b font-medium dark:border-neutral-500">
                <tr>
                  <th class="px-3 py-2 text-left sticky left-0 z-20 bg-gray-50 border-r border-gray-200">#</th>
                  <th v-for="main in props.mainSizes" :key="'header-' + main" class="px-3 py-2 text-left">
                    {{ main }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-if="props.optSizes.length > 0">
                  <tr v-for="opt in props.optSizes" :key="'row-' + opt"
                    class="border-b border-gray-200 hover:bg-gray-100">
                    <td class="px-3 py-2 sticky left-0 z-10 bg-white border-r border-gray-200">{{ opt }}</td>
                    <td v-for="main in props.mainSizes" :key="'cell-' + main + '-' + opt" class="px-3 py-2">
                      <input type="number" min="0"
                        class="w-20 border border-gray-300 rounded px-1 py-0.5 text-right focus:outline-none focus:ring-1 focus:ring-gray-400"
                        v-model="form.quantities[main][opt]" />
                    </td>
                  </tr>
                </template>

                <template v-else>
                  <tr class="border-b border-gray-200 hover:bg-gray-100">
                    <td class="px-3 py-2 sticky left-0 z-10 bg-white border-r border-gray-200">Qty</td>
                    <td v-for="main in props.mainSizes" :key="'cell-' + main + '-Qty'" class="px-3 py-2">
                      <input type="number" min="0"
                        class="w-20 border border-gray-300 rounded px-1 py-0.5 text-right focus:outline-none focus:ring-1 focus:ring-gray-400"
                        v-model.number="form.quantities[main]['Qty']" />
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

        </div>
      </form>
    </template>

    <template #footer>
      <SecondaryButton @click="emit('close')">
        Cancel
      </SecondaryButton>
      <PrimaryButton type="button" class="btn-green ml-2" @click="submitInventory">
        Add Unit
      </PrimaryButton>
    </template>

  </DialogModal>
</template>
